import { Marker, Popup } from "react-leaflet";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import "../../styles/mapStyles/Popup.css";
import SoilPopupContent from "./SoilPopupContent";
import usePlaceName from "../../hooks/usePlaceName";
import useSoilData from "../../hooks/useSoilData";

function UserMarker({ userLocation }) {
  const { t } = useTranslation();
  // States
  const [disableGetInfo, setDisableGetInfo] = useState(false);

  // Get the human-readable place name for the user's coordinates using a custom hook that uses TanStack Query for caching and state management.
  const { placeName } = usePlaceName(userLocation);

  // Custom hook using TanStack Query to fetch soil data and recommendations from Supabase when the user clicks "Get Soil Info"
  const { 
    fetchSoilData, 
    soilData, 
    recommendation, 
    isExactMatch,
    confidenceScore,        
    confidenceInterpretation, 
    pointsUsed,            
    isInterpolated,        
    isLoadingSoilData, 
    soilDataError } 
    = useSoilData();

  // Fetch soil information
  const handleGetSoilInfo = async () => {
    if (!userLocation) return;
    const [lat, lng] = userLocation;

    try {
      await fetchSoilData({ lat, lng });
    } catch (err) {
      return;
    }

    // Hide the button after a successful fetch
    setDisableGetInfo(true);
  };

  return (
    <Marker position={userLocation}>
      <Popup
        maxWidth={300}
        keepInView={true}
        autoPanPaddingTopLeft={[20, 80]}
        autoPanPaddingBottomRight={[20, 20]}
      >
        <div className="popup-container">
          <div className="popup-header">
            {/* Render the user's location */}
            <strong className="popup-title">📍 {t("map.yourLocation")}</strong>
            <p className="place-name">{placeName}</p>
            <p className="coordinates">
              {userLocation[0].toFixed(5)}°N, {userLocation[1].toFixed(5)}°E
            </p>

            {/* Divider line */}
            <hr className="divider" />
          </div>

          <div className="popup-scroll-body">
            {/* Shared popup body — handles buttons, loading, errors, results */}
            <SoilPopupContent
              placeName={placeName}
              coordinates={userLocation}
              soilData={soilData}
              recommendation={recommendation}
              loading={isLoadingSoilData}
              error={soilDataError}
              disableGetInfo={disableGetInfo}
              onGetSoilInfo={handleGetSoilInfo}
              isExactMatch={isExactMatch}
              confidenceScore={confidenceScore}
              confidenceInterpretation={confidenceInterpretation}
              pointsUsed={pointsUsed}
              isInterpolated={isInterpolated}
            />
          </div>
        </div>
      </Popup>
    </Marker>
  );
}

export default UserMarker;
